<template>
  <div v-if="ecmDocument" :class="`card-news-ecm card-news-ecm--${attributes.layout}`">
    <section v-if="isImage" class="card-news-ecm__image">
      <img :src="ecmDocument.content" alt="Image" />
    </section>

    <section v-if="!isImage && !isVideo" class="card-news-ecm__icon">
      <v-icon large>{{ ecmDocument.icon }}</v-icon>
    </section>

    <section v-if="!isImage && isVideo" class="card-news-ecm__video">
      <vue-plyr v-if="attributes.layout === 'text'">
        <video width="100%" height="250px" preload="metadata">
          <source :src="ecmDocument.content" :type="ecmDocument.contentType" />
        </video>
      </vue-plyr>

      <section v-if="attributes.layout === 'hover'" class="card-news-ecm__icon">
        <v-icon size="80">play_circle_outline</v-icon>
      </section>
    </section>

    <article>
      <h4 v-if="attributes.title" :class="attributes.titleStyleClasses">{{ model.title }}</h4>
      <p v-if="attributes.description" :class="attributes.descriptionStyleClasses">
        {{ doTruncateText(model.description, attributes.descriptionMaxLenght || 120) }}
      </p>
    </article>

    <section class="card-news-ecm__actions">
      <v-tooltip v-if="!isImage && isVideo && attributes.layout === 'hover'" bottom color="primary">
        <template v-slot:activator="{ on }">
          <v-btn
            data-has-tooltip="true"
            data-tooltip="Assistir vídeo"
            class="button-ecm button-ecm--play"
            v-on="on"
            small
            icon
            @click="handleOpenDialog"
          >
            <v-icon small color="white">play_arrow</v-icon>
          </v-btn>
        </template>
        <span>Assistir vídeo</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            data-has-tooltip="true"
            data-tooltip="Download"
            class="button-ecm button-ecm--download"
            v-on="on"
            small
            icon
            @click="downloadDocument"
          >
            <v-icon small color="white">get_app</v-icon>
          </v-btn>
        </template>
        <span>Download</span>
      </v-tooltip>
    </section>

    <v-dialog v-model="dialog" width="768">
      <vue-plyr>
        <video width="100%" preload="metadata">
          <source :src="ecmDocument.content" :type="ecmDocument.contentType" />
        </video>
      </vue-plyr>
    </v-dialog>
  </div>
</template>

<script>
import ECM from '@/services/ecm';
import { downloadFile, formatBytes, getIcon, doTruncateText } from '@/helpers/utils';

export default {
  props: {
    model: { type: [Object, Array], default: () => ({}) },
    attributes: { type: Object, default: () => ({}) }
  },

  data: () => ({
    isImage: false,
    isVideo: false,
    ecmDocument: null,
    dialog: false
  }),

  created() {
    this.createThumbnail();
  },

  methods: {
    doTruncateText,
    async createThumbnail() {
      const { data: dataGetDocumentDetail } = await ECM.GetDocumentDetail(this.getHash());
      const { data: dataReturnImageContent } = await ECM.ReturnImageContent(this.getHash());

      this.ecmDocument = {
        content: dataReturnImageContent,
        kBytesNumber: formatBytes(dataGetDocumentDetail.kBytesNumber),
        fileName: dataGetDocumentDetail.fileName,
        contentType: dataGetDocumentDetail.contentType,
        icon: getIcon(dataGetDocumentDetail.contentType)
      };

      this.isImage = dataGetDocumentDetail.contentType.includes('image/');
      this.isVideo = dataGetDocumentDetail.contentType.includes('video/');
    },

    getHash() {
      const hash = this.model.id_document.toString().padStart(9, '0');

      return `${hash}${this.model.nm_key}`;
    },

    async downloadDocument() {
      const { data } = await ECM.DownloadDocument(this.getHash());

      downloadFile(data.content, data.contentType, data.fileName.split('.').pop(), data.fileName.split('.').shift());
    },

    handleOpenDialog() {
      this.dialog = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.button-ecm {
  transition: all ease-in-out 0.3s;
  background-color: rgba(0, 0, 0, 0.25);
  margin-left: 10px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.4);
  }
}

.card-news-ecm {
  > article {
    h4,
    p {
      transition: all ease-in-out 0.3s;
    }

    > h4 {
      font-size: 18px;
      font-weight: bold;
      display: inline-block;

      &:hover {
        color: var(--v-primary-base);
      }
    }
  }

  .card-news-ecm__actions {
    display: flex;

    position: absolute;
    top: 20px;
    right: 20px;
  }

  .card-news-ecm__image {
    width: 100%;
    max-height: 250px;
    overflow: hidden;

    > img {
      width: calc(100% + 20px);
      max-width: calc(100% + 20px);
      height: 250px;
      transition: transform ease-in-out 0.3s;
      object-fit: cover;
    }
  }

  .card-news-ecm__icon {
    width: 100%;
    height: 250px;
    background: #ccc;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.card-news-ecm--hover {
    position: relative;

    &::before {
      position: absolute;
      content: '';
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.8) 100%);
      z-index: 1;
      transition: all ease-in-out 0.3s;
      pointer-events: none;
    }

    > article {
      position: absolute;
      bottom: 0;
      color: #fff;
      padding: 60px 30px 20px;
      z-index: 2;
    }

    &:hover {
      .card-news-ecm__image {
        > img {
          transform: translateX(-10px);
        }
      }

      &::before {
        background-color: rgba(0, 0, 0, 0.2);
      }

      > article {
        h4,
        p {
          transform: translateY(-10px);
        }
      }
    }
  }

  &.card-news-ecm--text {
    width: 100%;

    .card-news-ecm__image {
      position: relative;

      &::before {
        position: absolute;
        content: '';
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-color: rgba(0, 0, 0, 0);
        z-index: 1;
        transition: all ease-in-out 0.3s;
        pointer-events: none;
      }
    }

    > article {
      padding: 20px 30px;
    }

    &:hover {
      .card-news-ecm__image {
        &::before {
          background-color: rgba(0, 0, 0, 0.2);
        }

        > img {
          transform: translateX(-10px);
        }
      }

      > article > h4 {
        color: var(--v-primary-base);
      }
    }
  }
}
</style>
